import { IndexedDbStorage } from "./indexedDbStorage";
import * as logger from "../logger";
import { DRIVE_STORAGE_PREFIX, getFullReducerStorageKey } from "../../store/storageConstants";
import { isSecureStorageReducer, REDUCER_KEYS_STILL_IN_LOCALSTORAGE } from "../../store/reducerPersistence";
import { DriveStore, IGlobalReducer } from "../../store/reducers";
import { FeatureFlagName } from "../featureFlagName";
import { isNativeStorageAvailable } from "../native/secureStorageApi";
import { isIphone } from "../native/nativeHelpers";
import storage from "./storage";

/**
 * Set of reducer keys that were modified during migration/rollback.
 * We only store keys, not values, because both migration and rollback
 * read from Redux state (in-memory) as their source of truth.
 */
type QueuedWrites = Set<keyof IGlobalReducer>;

/**
 * Persistent tracking for migration/rollback state and attempts.
 * Stored in localStorage to survive app refreshes for debugging and monitoring.
 * When `version` is set, IndexedDB is in use; when absent, LocalStorage is in use.
 */
export interface MigrationState {
    version?: string;       // When set (e.g., "1"), IndexedDB is in use
    attemptCount: number;
    lastAttempt: string;    // ISO timestamp
    lastError?: string;
    direction: "migration" | "rollback";
}

/**
 * Handles migration of data between Redux store and IndexedDB storage systems.
 *
 * This class provides utilities to migrate data from Redux store to IndexedDB and
 * rollback the migration to LocalStorage if needed.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class IndexedDbMigrationFromRedux {
    static readonly idbVersion = "1";
    /**
     * When migration or rollback in progress it will return a Promise, otherwise null (falsy value).
     */
    static activeMigrationOrRollback: Promise<any> | null = null;
    // Lazy cache to avoid repeated localStorage reads. Null means not yet checked.
    private static cachedIsIndexedDbInUse: boolean | null = null;
    private static readonly writeQueue: QueuedWrites = new Set();

    static get isIndexedDbInUse(): boolean {
        // Lazy cache: check localStorage only if not yet cached
        if (this.cachedIsIndexedDbInUse === null) {
            const state = this.getMigrationState();
            this.cachedIsIndexedDbInUse = state?.version === this.idbVersion;
        }
        return this.cachedIsIndexedDbInUse;
    }

    static get migrationStateKey() { return `${ DRIVE_STORAGE_PREFIX }@indexedDb_migration_state`; }

    /**
     * Retrieves the current migration state from localStorage.
     * Returns null if no state exists (first attempt or cleared after success).
     */
    static getMigrationState(): MigrationState | null {
        try {
            const stateJson = localStorage.getItem(this.migrationStateKey);
            if (!stateJson) {
                return null;
            }
            return JSON.parse(stateJson) as MigrationState;
        } catch (error) {
            logger.error(logger.Source.INDEXED_DB, `Failed to parse migration state: ${ error }`);
            return null;
        }
    }

    /**
     * Updates the migration state in localStorage.
     * Merges the update with existing state if present.
     */
    static updateMigrationState(update: Partial<MigrationState>): void {
        try {
            const current = this.getMigrationState() || { attemptCount: 0, lastAttempt: "", direction: "migration" as const };
            const updated = { ...current, ...update };
            localStorage.setItem(this.migrationStateKey, JSON.stringify(updated));
        } catch (error) {
            // Non-critical - logging will still work, just without persistent state
            logger.error(logger.Source.INDEXED_DB, `Failed to update migration state: ${ error }`);
        }
    }

    /**
     * Clears the migration state from localStorage.
     * Called on successful migration/rollback to reset attempt tracking.
     */
    static clearMigrationState(): void {
        try {
            localStorage.removeItem(this.migrationStateKey);
        } catch (error) {
            logger.error(logger.Source.INDEXED_DB, `Failed to clear migration state: ${ error }`);
        }
    }

    /**
     * Only for tests
     */
    static setIndexedDbInUse(inUse: boolean) {
        if (inUse) {
            IndexedDbMigrationFromRedux.setIndexDbVersion();
        } else {
            IndexedDbMigrationFromRedux.removeIndexedDbVersion();
        }
    }

    static setIndexDbVersion() {
        this.updateMigrationState({ version: this.idbVersion, lastError: undefined });
        this.cachedIsIndexedDbInUse = true;
    }

    static removeIndexedDbVersion() {
        this.updateMigrationState({ version: undefined, lastError: undefined });
        this.cachedIsIndexedDbInUse = false;
    }


    /**
     * Queues a reducer key to be re-written after migration/rollback completes.
     * This prevents data loss when Redux state updates during migration/rollback.
     * We only store the reducer key (e.g., "userHosLogs"), not the value,
     * because we read from Redux state (in-memory) when flushing the queue.
     */
    static queueWrite(reducerKey: keyof IGlobalReducer): void {
        this.writeQueue.add(reducerKey);
        logger.log(logger.Source.INDEXED_DB, `Migration: Queued write for reducer: ${ reducerKey }`);
    }

    static async migrateIfNeeded(store: DriveStore): Promise<void> {
        // On iOS, skip migration if the app is in the background to avoid AbortError.
        // WKWebView aborts active IndexedDB transactions while backgrounded.
        // The launcher re-calls migrateIfNeeded on every foreground event (isIphone guard),
        // so any missed trigger will be retried when the app returns to the foreground.
        if (isIphone() && store.getState().mobile.background) {
            logger.log(logger.Source.INDEXED_DB, "Migration skipped: iOS app is in background.");
            return;
        }

        const ffs = store.getState().data.featureFlags;
        const indexedDbJustEnabled = ffs[FeatureFlagName.ENABLE_INDEXED_DB_STORE]?.isEnabled;

        // The FF got enabled and IndexedDB is not in use -> migrate
        if (indexedDbJustEnabled && !IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            if (!IndexedDbStorage.isIndexDbSupported()) {
                logger.log(logger.Source.INDEXED_DB, `Migration: IndexedDB is not supported in this environment.`);
                return;
            }
            await IndexedDbMigrationFromRedux.migrate(store);
        } else if (!indexedDbJustEnabled && IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            // The FF got disabled and IndexedDB is in use -> rollback
            await IndexedDbMigrationFromRedux.rollback(store);
        }
    }


    /**
     * Migrates data from Redux state to IndexedDB.
     *
     * Safety features:
     * - Deletes existing IndexedDB database to ensure clean state (prevents data divergence)
     * - Reads from Redux state (source of truth)
     * - Retains LocalStorage data until migration succeeds
     * - Processes queued writes after migration
     */
    static async migrate(store: DriveStore): Promise<any> {
        const log = (message: string) => {
            logger.log(logger.Source.INDEXED_DB, `Migration: ${ message }`);
        };

        const errorLog = (message: string) => {
            logger.error(logger.Source.INDEXED_DB, `Migration: ${ message }`);
        };

        if (!IndexedDbStorage.isIndexDbSupported()) {
            log(`IndexedDB is not supported in this environment.`);
            return Promise.resolve();
        }

        if (IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            log("IndexedDB already in use.");
            return Promise.resolve();
        }

        // If it's already been started, return it
        if (IndexedDbMigrationFromRedux.activeMigrationOrRollback) {
            return IndexedDbMigrationFromRedux.activeMigrationOrRollback;
        }

        // Track migration attempt
        const previousState = this.getMigrationState();
        const attemptCount = (previousState?.direction === "migration" ? previousState.attemptCount : 0) + 1;
        this.updateMigrationState({
            attemptCount,
            lastAttempt: new Date().toISOString(),
            direction: "migration",
            lastError: undefined
        });

        let migrationFailures: string[] = [];
        IndexedDbMigrationFromRedux.activeMigrationOrRollback = (async () => {
            try {
                // Collect all reducer state from Redux store
                const itemsToMigrate = IndexedDbMigrationFromRedux.getItemsToMigrate(store);
                const migratedKeys: string[] = [];

                if (itemsToMigrate.size === 0) {
                    const lsDiagnostic = IndexedDbMigrationFromRedux.getLocalStorageDiagnostic();
                    const msg = `No items found in Redux state to migrate. ${ lsDiagnostic }`;
                    errorLog(`${ msg } [ATTEMPT ${ attemptCount }]`);
                    migrationFailures.push(msg);
                    return;
                }

                log(`Starting migration of ${ itemsToMigrate.size } items from LocalStorage to IndexedDB. [ATTEMPT ${ attemptCount }]`);

                for (const [key, value] of Array.from(itemsToMigrate.entries())) {
                    try {
                        await IndexedDbStorage.saveData(key, value);
                        migratedKeys.push(key);
                    } catch (error) {
                        migrationFailures.push(`${ key } due to ${ error }`);
                    }
                }

                if (migrationFailures.length === 0) {
                    try {
                        await this.processQueue(store, true);
                        log("Processed queued writes after migration.");
                    } catch (error) {
                        migrationFailures.push(`queued writes due to ${ error }`);
                    }
                }

                if (migrationFailures.length === 0) {
                    try {
                        IndexedDbMigrationFromRedux.setIndexDbVersion();
                        log("IndexedDB version set successfully.");
                    } catch (error) {
                        migrationFailures.push(`setting IndexedDB version due to ${ error }`);
                    }
                }

                if (migrationFailures.length === 0) {
                    for (const key of migratedKeys) {
                        // Retain keys that must stay in localStorage even when IndexedDB is in use
                        // (e.g. for add-in compatibility and sync reads). See REDUCER_KEYS_STILL_IN_LOCALSTORAGE.
                        const keyWithoutPrefix = key.slice(DRIVE_STORAGE_PREFIX.length);
                        if (REDUCER_KEYS_STILL_IN_LOCALSTORAGE.has(keyWithoutPrefix)) {
                            continue;
                        }
                        try {
                            localStorage.removeItem(key);
                        } catch (error) {
                            errorLog(`Failed to remove migrated LocalStorage key ${ key }: ${ error }`);
                        }
                    }
                    log(`SUCCESS - Migrated ${ itemsToMigrate.size } items. [ATTEMPT ${ attemptCount }]`);
                }
            } catch (error) {
                migrationFailures.push(`General migration failure due to ${ error }`);
            } finally {
                if (migrationFailures.length > 0) {
                    const errorMessage = migrationFailures.join(", ");
                    errorLog(`FAILED - ${ errorMessage } [ATTEMPT ${ attemptCount }]`);
                    this.updateMigrationState({ lastError: errorMessage });
                }
                IndexedDbMigrationFromRedux.activeMigrationOrRollback = null;
            }
        })();

        return IndexedDbMigrationFromRedux.activeMigrationOrRollback;
    }

    /**
     * Rolls back from IndexedDB to LocalStorage.
     *
     * Safety features:
     * - Reads from Redux state as source of truth (handles failed writes, queued writes, partial migrations)
     * - Writes to LocalStorage
     * - Attempts to clean up IndexedDB (non-blocking if it fails)
     * - Processes any queued writes that came in during rollback
     */
    static async rollback(store: DriveStore) {
        const log = (message: string) => {
            logger.log(logger.Source.INDEXED_DB, `Rollback: ${ message }`);
        };

        const errorLog = (message: string) => {
            logger.error(logger.Source.INDEXED_DB, `Rollback: ${ message }`);
        };

        if (!IndexedDbStorage.isIndexDbSupported()) {
            log(`IndexedDB is not supported in this environment.`);
            return;
        }

        if (!IndexedDbMigrationFromRedux.isIndexedDbInUse) {
            log(`IndexedDB not in use, skipping rollback.`);
            return;
        }

        if (IndexedDbMigrationFromRedux.activeMigrationOrRollback) {
            return IndexedDbMigrationFromRedux.activeMigrationOrRollback;
        }

        // Track rollback attempt
        const previousState = this.getMigrationState();
        const attemptCount = (previousState?.direction === "rollback" ? previousState.attemptCount : 0) + 1;
        this.updateMigrationState({
            attemptCount,
            lastAttempt: new Date().toISOString(),
            direction: "rollback",
            lastError: undefined
        });

        IndexedDbMigrationFromRedux.activeMigrationOrRollback = (async () => {
            let rollbackFailures: string[] = [];
            try {
                // Collect all reducer state from Redux store
                const itemsToMigrate = IndexedDbMigrationFromRedux.getItemsToMigrate(store);

                if (itemsToMigrate.size === 0) {
                    const lsDiagnostic = IndexedDbMigrationFromRedux.getLocalStorageDiagnostic();
                    const msg = `No items found in Redux state to roll back. ${ lsDiagnostic }`;
                    errorLog(`${ msg } [ATTEMPT ${ attemptCount }]`);
                    rollbackFailures.push(msg);
                    return;
                }

                log(`Starting rollback of ${ itemsToMigrate.size } items from IndexedDB to LocalStorage. [ATTEMPT ${ attemptCount }]`);

                // Write each reducer to LocalStorage
                for (const [key, value] of Array.from(itemsToMigrate.entries())) {
                    if (value === undefined || value === null) {
                        continue;
                    }

                    try {
                        // Use storage utility (handles compression, quota, error reporting)
                        // Note: key is already the full storage key from getItemsToMigrate()
                        storage.set(key, value, undefined, true);
                    } catch (error) {
                        //TODO: Consider stopping the rollback if any write fails & undo previous writes.
                        // Then feature flag wouldn't be enabled, but data would remain in IndexedDB
                        rollbackFailures.push(`${ key } due to ${ error }`);
                    }
                }

                if (rollbackFailures.length === 0) {
                    log(`SUCCESS - Rolled back ${ itemsToMigrate.size } items. [ATTEMPT ${ attemptCount }]`);

                    // Try delete IndexedDB database now that localStorage has all the data
                    const dbExists = await IndexedDbStorage.checkOurDatabaseExists();
                    if (dbExists) {
                        try {
                            await IndexedDbStorage.clearAllData();
                            log("Successfully deleted IndexedDB database.");
                        } catch (error) {
                            errorLog(`Failed to delete IndexedDB database (non-critical): ${ error }`);
                            // Continue anyway - LocalStorage has the correct data
                        }
                    } else {
                        log("No IndexedDB database to delete.");
                    }
                }

            } catch (error) {
                rollbackFailures.push(`General rollback failure due to ${ error }`);
            } finally {
                if (rollbackFailures.length > 0) {
                    const errorMessage = rollbackFailures.join(", ");
                    errorLog(`FAILED - ${ errorMessage } [ATTEMPT ${ attemptCount }]`);
                    this.updateMigrationState({ lastError: errorMessage });
                }

                // Process any writes that were queued during rollback
                try {
                    // If any failures, don't process queue because we will stay with IndexedDB
                    if (rollbackFailures.length === 0) {
                        await this.processQueue(store, false);
                        log("Processed queued writes after rollback.");
                    }
                } catch (error) {
                    // Need logging of processing queue failure but doesn't necessarily need to block completing rollback
                    errorLog(`Error processing queued writes: ${ error } [ATTEMPT ${ attemptCount }]`);
                }

                // Remove the flag as the final step to mark rollback complete
                try {
                    if (rollbackFailures.length === 0) {
                        IndexedDbMigrationFromRedux.removeIndexedDbVersion();
                        log(`Removed IndexedDB version successfully. [ATTEMPT ${ attemptCount }]`);
                    }
                } catch (error) {
                    errorLog(`Error removing IndexedDB flag: ${ error } [ATTEMPT ${ attemptCount }]`);
                }

                IndexedDbMigrationFromRedux.activeMigrationOrRollback = null;
            }
        })();

        return IndexedDbMigrationFromRedux.activeMigrationOrRollback;
    }

    /**
     * Returns a short diagnostic string with the total LocalStorage key count.
     * Useful for recording context when Redux state is unexpectedly empty.
     */
    private static getLocalStorageDiagnostic(): string {
        try {
            return `LocalStorage has ${ localStorage.length } total key(s)`;
        } catch {
            return "LocalStorage not accessible";
        }
    }

    private static getItemsToMigrate(store: DriveStore) {
        const state = store.getState();
        const itemsToMigrate = new Map<string, any>();
        for (const reducerKey in state) {
            if (!state.hasOwnProperty(reducerKey)) {
                continue;
            }

            if (this.shouldSkipReducerDuringStorageMigration(reducerKey as keyof IGlobalReducer)) {
                continue;
            }

            const fullKey = getFullReducerStorageKey(reducerKey as any);
            const value = state[reducerKey];

            if (value !== undefined && value !== null) {
                // TODO: Consider structuredClone / JSON.parse(JSON.stringify()) for deep copies
                itemsToMigrate.set(fullKey as keyof IGlobalReducer, value);
            }
        }
        return itemsToMigrate;
    }

    private static shouldSkipReducerDuringStorageMigration(reducerKey: keyof IGlobalReducer): boolean {
        return isNativeStorageAvailable() && isSecureStorageReducer(reducerKey);
    }

    /**
     * Processes all queued reducer writes after migration/rollback completes.
     * Reads fresh values from Redux state for each queued reducer key.
     * Writes to IndexedDB or LocalStorage based on the target storage system.
     * Recursively processes until the queue is empty to handle writes that
     * occur during queue processing.
     *
     * @param store - The Redux store to read current state from
     * @param useIndexedDb - True to write to IndexedDB, false for LocalStorage
     * @param depth - Recursion depth counter to prevent infinite loops
     */
    private static async processQueue(store: DriveStore, useIndexedDb: boolean, depth = 0): Promise<void> {
        if (depth > 10) {
            logger.log(logger.Source.INDEXED_DB, `Process queue recursion depth exceeded limit. Stopping processing. [DEPTH ${ depth }]`);
            return;
        }

        if (this.writeQueue.size === 0) {
            return;
        }

        const target = useIndexedDb ? "IndexedDB" : "LocalStorage";
        logger.log(logger.Source.INDEXED_DB, `Processing ${ this.writeQueue.size } queued writes to ${ target }. [DEPTH ${ depth }]`);

        // Capture current queue and clear it immediately
        // This allows new writes to queue while we process the current batch
        const currentBatch = Array.from(this.writeQueue);
        this.writeQueue.clear();

        // Read fresh values from Redux state for each queued reducer
        const state = store.getState();
        let processFailures: string[] = [];

        for (const reducerKey of currentBatch) {
            const value = state[reducerKey];

            if (value === undefined || value === null) {
                continue;
            }

            const fullKey = getFullReducerStorageKey(reducerKey);

            try {
                if (useIndexedDb) {
                    await IndexedDbStorage.saveData(fullKey, value);
                } else {
                    // Write to LocalStorage (forceKey=true means don't add prefix, key is already full)
                    storage.set(fullKey, value, undefined, true);
                }
            } catch (error) {
                processFailures.push(reducerKey);
                logger.error(logger.Source.INDEXED_DB, `Failed to process queued write for reducer: ${ reducerKey } [DEPTH ${ depth }]`);
            }
        }

        if (processFailures.length > 0) {
            logger.error(logger.Source.INDEXED_DB, `Failed to process ${ processFailures.length } queued writes. [DEPTH ${ depth }]`);
        } else {
            logger.log(logger.Source.INDEXED_DB, `Successfully processed all queued writes to ${ target }. [DEPTH ${ depth }]`);
        }

        // Recursively process any writes that were queued during this batch
        if (this.writeQueue.size > 0) {
            logger.log(logger.Source.INDEXED_DB, `New writes queued during processing. Recursively processing... [DEPTH ${ depth }]`);
            await this.processQueue(store, useIndexedDb, depth + 1);
        }
    }
}
